import { useState, useEffect, useRef } from 'react'
import {
    Plus,
    Trash2,
    ExternalLink,
    Settings2,
    Package,
    Link as LinkIcon,
    ToggleLeft,
    ToggleRight,
    Edit2,
    Check,
    X,
    ChevronDown,
    ChevronUp,
    Search,
    Loader2,
    Users,
    Activity,
    Save,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { VisibilityKeywordsCitiesSettings } from '@/components/workspace/VisibilityKeywordsCitiesSettings'
import { cn } from '@/lib/utils'
import { useSettings, type TrackedSKUWithCompetitors } from '@/contexts/SettingsContext'
import { useStore } from '@/contexts/StoreContext'
import { trpc } from '@/lib/trpc'
import type { Platform } from '@/types/settings'
import type { Product } from '@/types'

// Platform options for dropdown
const platformOptions: { value: Platform; label: string }[] = [
    { value: 'blinkit', label: 'Blinkit' },
    { value: 'zepto', label: 'Zepto' },
    { value: 'instamart', label: 'Instamart' },
    { value: 'bbnow', label: 'BBNow' },
]

// Parse Blinkit URL to extract product info
function parseBlinkitUrl(url: string): { productId: string; productName: string } | null {
    try {
        const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`
        const urlObj = new URL(normalizedUrl)
        if (!urlObj.hostname.includes('blinkit.com')) {
            return null
        }

        // Blinkit URLs: https://blinkit.com/prn/product-name/prid/12345
        const pathParts = urlObj.pathname.split('/').filter(Boolean)
        const pridIndex = pathParts.indexOf('prid')
        if (pridIndex !== -1 && pathParts[pridIndex + 1]) {
            const productId = pathParts[pridIndex + 1]
            // Extract product name from URL (between /prn/ and /prid/)
            const prnIndex = pathParts.indexOf('prn')
            const productName = prnIndex !== -1 && prnIndex < pridIndex
                ? decodeURIComponent(pathParts.slice(prnIndex + 1, pridIndex).join(' ')).replace(/-/g, ' ')
                : ''
            return { productId, productName }
        }
    } catch {
        // Invalid URL
    }
    return null
}

// Parse Instamart URL to extract product info
// Instamart URLs: https://www.swiggy.com/instamart/product/product-name/PRODUCTID or similar
function parseInstamartUrl(url: string): { productId: string; productName: string } | null {
    try {
        const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`
        const urlObj = new URL(normalizedUrl)
        if (!urlObj.hostname.includes('swiggy.com')) {
            return null
        }

        // Swiggy Instamart URLs: various formats
        // e.g. https://www.swiggy.com/instamart/product/product-name/PRODUCTID
        const pathParts = urlObj.pathname.split('/').filter(Boolean)
        const instamartIdx = pathParts.indexOf('instamart')
        if (instamartIdx !== -1) {
            const productIdx = pathParts.indexOf('product', instamartIdx)
            if (productIdx !== -1 && pathParts.length > productIdx + 1) {
                // Last segment is likely the product ID
                const lastSegment = pathParts[pathParts.length - 1]
                // Product name is between 'product' and the last segment
                const nameParts = pathParts.slice(productIdx + 1, pathParts.length - 1)
                const productName = nameParts.length > 0
                    ? decodeURIComponent(nameParts.join(' ')).replace(/-/g, ' ')
                    : ''
                return { productId: lastSegment, productName }
            }
        }
    } catch {
        // Invalid URL
    }
    return null
}

// Check if input looks like a URL (supports both Blinkit and Instamart)
function isUrl(input: string): boolean {
    return input.startsWith('http://') || input.startsWith('https://') || input.includes('blinkit.com') || input.includes('swiggy.com')
}

function mapSimilarProductToProduct(details: {
    id: string
    name: string
    brand: string | null
    category: string | null
    imageUrl: string | null
    rating: number | null
    ratingCount: number | null
    unit: string | null
    parentProductId?: string
}): Product {
    return {
        id: details.id,
        name: details.name,
        brand: details.brand || '',
        category: details.category || '',
        imageUrl: details.imageUrl || '',
        rating: details.rating ?? undefined,
        reviewCount: details.ratingCount ?? undefined,
        unit: details.unit || undefined,
        parentProductId: details.parentProductId,
    }
}

// Competitor type for internal use
interface CompetitorProduct {
    id: string
    name: string
    brand: string
    imageUrl: string
    category?: string
    price?: number
    mrp?: number
    discountPercent?: number
    rating?: number
    ratingCount?: number
    quantity?: string
    unit?: string
    /** Instamart-specific: parent product ID */
    parentProductId?: string
}

// Form data type
interface AddSKUFormData {
    sku: string
    name: string
    link: string
    platform: Platform
    brand?: string
    category?: string
    imageUrl?: string
    price?: number
    mrp?: number
    discountPercent?: number
    /** Platform-specific metadata (e.g. Instamart parentProductId) */
    metadata?: Record<string, unknown>
}

// Add SKU Form Component with unified search/URL input
function AddSKUForm({ onSubmit, onCancel, userBrand, workspaceId }: {
    onSubmit: (data: AddSKUFormData, competitors?: CompetitorProduct[]) => void
    onCancel: () => void
    userBrand: string
    workspaceId: string | null
}) {
    const { selectedStore } = useStore()
    const [formData, setFormData] = useState<AddSKUFormData>({
        sku: '',
        name: '',
        link: '',
        platform: 'blinkit',
        brand: '',
        category: '',
        imageUrl: '',
    })

    // Unified search/URL input state
    const [searchInput, setSearchInput] = useState('')
    const [searchResults, setSearchResults] = useState<Product[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    // tRPC utils for imperative query calls + mutation for category lookup
    const utils = trpc.useUtils()
    const lookupCategoryMutation = trpc.categories.lookup.useMutation()

    // Competitor state
    const [competitors, setCompetitors] = useState<CompetitorProduct[]>([])
    const [selectedCompetitors, setSelectedCompetitors] = useState<Set<string>>(new Set())
    const [loadingCompetitors, setLoadingCompetitors] = useState(false)
    const [competitorSearchQuery, setCompetitorSearchQuery] = useState('')
    const [competitorSuggestions, setCompetitorSuggestions] = useState<Product[]>([])
    const [showCompetitorDropdown, setShowCompetitorDropdown] = useState(false)
    const competitorInputRef = useRef<HTMLInputElement>(null)

    // Active platform for the add form (Blinkit or Instamart)
    const [activePlatform, setActivePlatform] = useState<'blinkit' | 'instamart'>('blinkit')

    // Reset form when platform changes
    const handlePlatformChange = (platform: 'blinkit' | 'instamart') => {
        if (platform === activePlatform) return
        setActivePlatform(platform)
        setSelectedProduct(null)
        setSearchInput('')
        setSearchResults([])
        setShowDropdown(false)
        setCompetitors([])
        setSelectedCompetitors(new Set())
        setCompetitorSearchQuery('')
        setCompetitorSuggestions([])
        setFormData({
            sku: '',
            name: '',
            link: '',
            platform,
            brand: '',
            category: '',
            imageUrl: '',
        })
    }

    // Handle unified search input - detects URL or search query (platform-aware)
    useEffect(() => {
        if (selectedProduct) {
            return
        }

        const trimmedInput = searchInput.trim()
        if (trimmedInput.length < 2) {
            setSearchResults([])
            return
        }

        let isCancelled = false

        // Check if it's a URL
        if (isUrl(trimmedInput)) {
            // Try Blinkit URL first
            const blinkitParsed = parseBlinkitUrl(trimmedInput)
            if (blinkitParsed) {
                setIsSearching(true)
                setShowDropdown(false)

                utils.comparison.getDetails.fetch({
                    productId: blinkitParsed.productId,
                    platform: 'blinkit',
                    lat: selectedStore.latitude,
                    lon: selectedStore.longitude,
                }).then(details => {
                    if (isCancelled) return

                    if (details) {
                        // Switch to blinkit platform if URL is blinkit
                        if (activePlatform !== 'blinkit') setActivePlatform('blinkit')
                        handleSelectProduct(mapSimilarProductToProduct(details), trimmedInput)
                        return
                    }

                    setFormData(prev => ({
                        ...prev,
                        sku: blinkitParsed.productId,
                        name: blinkitParsed.productName || blinkitParsed.productId,
                        link: trimmedInput,
                        platform: 'blinkit',
                    }))
                }).catch(error => {
                    console.error('Failed to fetch Blinkit product details from URL:', error)
                }).finally(() => {
                    if (!isCancelled) setIsSearching(false)
                })

                return () => { isCancelled = true }
            }

            // Try Instamart URL
            const instamartParsed = parseInstamartUrl(trimmedInput)
            if (instamartParsed) {
                setIsSearching(true)
                setShowDropdown(false)

                // For Instamart URLs, we only have productId - set form data directly
                // (Instamart product details require parentProductId which URL may not have)
                if (activePlatform !== 'instamart') setActivePlatform('instamart')
                setFormData(prev => ({
                    ...prev,
                    sku: instamartParsed.productId,
                    name: instamartParsed.productName || instamartParsed.productId,
                    link: trimmedInput,
                    platform: 'instamart',
                }))
                setIsSearching(false)

                return () => { isCancelled = true }
            }

            return () => { isCancelled = true }
        }

        // Regular search - route to correct platform
        const timer = setTimeout(async () => {
            setIsSearching(true)
            try {
                const result = await utils.search.products.fetch({
                    query: trimmedInput,
                    platform: activePlatform,
                    lat: selectedStore.latitude,
                    lon: selectedStore.longitude,
                })
                if (isCancelled) return
                setSearchResults(result.products as Product[])
                setShowDropdown(true)
            } catch (error) {
                console.error('Search failed:', error)
            } finally {
                if (!isCancelled) setIsSearching(false)
            }
        }, 300)

        return () => {
            isCancelled = true
            clearTimeout(timer)
        }
    }, [searchInput, selectedProduct, selectedStore, activePlatform])

    // Debounced search for competitor suggestions (platform-aware)
    useEffect(() => {
        if (competitorSearchQuery.length < 2) {
            setCompetitorSuggestions([])
            return
        }

        const timer = setTimeout(async () => {
            try {
                const result = await utils.search.products.fetch({
                    query: competitorSearchQuery,
                    platform: activePlatform,
                    lat: selectedStore.latitude,
                    lon: selectedStore.longitude,
                })
                // Filter out products from user's brand and selected product's brand
                const selectedBrand = selectedProduct?.brand?.toLowerCase() || ''
                const filtered = result.products.filter(p => {
                    const productBrand = p.brand?.toLowerCase() || ''
                    return (
                        productBrand !== userBrand.toLowerCase() &&
                        productBrand !== selectedBrand &&
                        productBrand !== ''
                    )
                })
                setCompetitorSuggestions(filtered)
            } catch (error) {
                console.error('Competitor search failed:', error)
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [competitorSearchQuery, selectedStore, userBrand, selectedProduct, activePlatform])

    // Handle product selection (platform-aware)
    const handleSelectProduct = (product: Product, url?: string) => {
        setSelectedProduct(product)

        let productUrl: string
        const metadata: Record<string, unknown> = {}

        if (activePlatform === 'instamart') {
            const slug = product.name.toLowerCase().replace(/\s+/g, '-')
            productUrl = url || `https://www.swiggy.com/instamart/product/${slug}/${product.id}`
            if (product.parentProductId) {
                metadata.parentProductId = product.parentProductId
            }
        } else {
            productUrl = url || `https://blinkit.com/prn/${product.name.toLowerCase().replace(/\s+/g, '-')}/prid/${product.id}`
        }

        setFormData({
            sku: product.id,
            name: product.name,
            link: productUrl,
            platform: activePlatform,
            brand: product.brand || '',
            category: product.category || '', // Will be updated by loadCompetitorsForProduct
            imageUrl: product.imageUrl || '',
            metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        })
        setSearchInput(product.name)
        setShowDropdown(false)

        // Load competitors and product details (including category) using comparison API
        loadCompetitorsForProduct(product)
    }

    // Load competitors when a product is selected using the comparison API (platform-aware)
    // Also fetches the product's L0 category from product details
    const loadCompetitorsForProduct = async (product: Product) => {
        setLoadingCompetitors(true)
        try {
            // Build comparison request with platform-specific params
            const comparisonParams: {
                productId: string
                lat: number
                lon: number
                platform?: 'blinkit' | 'instamart'
                parentProductId?: string
            } = {
                productId: product.id,
                lat: selectedStore.latitude,
                lon: selectedStore.longitude,
                platform: activePlatform,
            }

            // Instamart requires parentProductId for product details
            if (activePlatform === 'instamart' && product.parentProductId) {
                comparisonParams.parentProductId = product.parentProductId
            }

            const comparison = await utils.comparison.getWithSimilar.fetch(comparisonParams)

            if (comparison) {
                const productCategory = comparison.product.category
                const mainProduct = comparison.product

                console.log('Product comparison response:', {
                    platform: activePlatform,
                    productCategory,
                    price: mainProduct.price,
                    mrp: mainProduct.mrp,
                    fullProduct: mainProduct,
                    similarProductsCount: comparison.similarProducts?.length || 0,
                    similarProducts: comparison.similarProducts,
                })

                // Start with category from comparison response
                let displayCategory = productCategory

                // Try to look up L1 category hierarchy using platform_categories table
                if (workspaceId && productCategory && productCategory !== 'Unknown') {
                    try {
                        const categoryMapping = await lookupCategoryMutation.mutateAsync({
                            workspaceId,
                            platform: activePlatform,
                            categoryName: productCategory,
                        })
                        if (categoryMapping?.l1) {
                            displayCategory = categoryMapping.l1
                        }
                    } catch (err) {
                        console.warn('Category lookup failed, using category from response:', err)
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    brand: mainProduct.brand || prev.brand || '',
                    category: (displayCategory && displayCategory !== 'Unknown')
                        ? displayCategory
                        : (productCategory && productCategory !== 'Unknown' ? productCategory : prev.category),
                    price: mainProduct.price || undefined,
                    mrp: mainProduct.mrp || undefined,
                    discountPercent: mainProduct.discountPercent || undefined,
                }))

                if (comparison.similarProducts) {
                    const selectedProductBrand = product.brand?.toLowerCase() || ''
                    const selectedProductName = product.name?.toLowerCase() || ''
                    const userBrandLower = userBrand.toLowerCase()

                    const filtered = comparison.similarProducts
                        .filter(p => {
                            if (p.id === product.id) return false

                            const productBrand = p.brand?.toLowerCase() || ''
                            const productName = p.name?.toLowerCase() || ''

                            if (productBrand) {
                                if (productBrand === userBrandLower) return false
                                if (selectedProductBrand && productBrand === selectedProductBrand) return false
                            }

                            if (userBrandLower && productName.includes(userBrandLower)) return false
                            if (selectedProductBrand && productName.includes(selectedProductBrand)) return false

                            const selectedWords = selectedProductName.split(' ').slice(0, 2).join(' ')
                            const productWords = productName.split(' ').slice(0, 2).join(' ')
                            if (selectedWords && productWords && selectedWords === productWords) return false

                            return true
                        })
                        .slice(0, 10)
                        .map((p): CompetitorProduct => ({
                            id: p.id,
                            name: p.name,
                            brand: p.brand || '',
                            imageUrl: p.imageUrl || '',
                            category: p.category || productCategory || '',
                            price: p.price || undefined,
                            mrp: p.mrp || undefined,
                            discountPercent: p.discountPercent || undefined,
                            rating: p.rating || undefined,
                            ratingCount: p.ratingCount || undefined,
                            quantity: p.quantity ? String(p.quantity) : undefined,
                            unit: p.unit || undefined,
                            parentProductId: (p as { parentProductId?: string }).parentProductId || undefined,
                        }))

                    console.log('Filtered competitors:', filtered.length, 'from', comparison.similarProducts.length)
                    setCompetitors(filtered)
                    setSelectedCompetitors(new Set(filtered.map(c => c.id)))
                }
            } else {
                setCompetitors([])
            }
        } catch (error) {
            console.error('Failed to load competitors:', error)
            setCompetitors([])
        } finally {
            setLoadingCompetitors(false)
        }
    }

    // Handle competitor toggle
    const handleCompetitorToggle = (product: CompetitorProduct) => {
        setSelectedCompetitors(prev => {
            const next = new Set(prev)
            if (next.has(product.id)) {
                next.delete(product.id)
            } else {
                next.add(product.id)
                if (!competitors.find(c => c.id === product.id)) {
                    setCompetitors(c => [...c, product])
                }
            }
            return next
        })
    }

    // Add competitor from search
    const handleAddCompetitorFromSearch = (product: Product) => {
        const comp: CompetitorProduct = {
            id: product.id,
            name: product.name,
            brand: product.brand || '',
            imageUrl: product.imageUrl || '',
            category: product.category || '',
        }
        if (!competitors.find(c => c.id === product.id)) {
            setCompetitors(c => [...c, comp])
        }
        setSelectedCompetitors(prev => new Set(prev).add(product.id))
        setCompetitorSearchQuery('')
        setShowCompetitorDropdown(false)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (formData.sku && formData.name && formData.link) {
            const selectedCompetitorProducts = competitors.filter(c => selectedCompetitors.has(c.id))
            console.log('[handleSubmit] formData:', formData)
            console.log('[handleSubmit] selectedCompetitorProducts:', selectedCompetitorProducts)
            onSubmit(formData, selectedCompetitorProducts)
        }
    }

    const handleClear = () => {
        setSelectedProduct(null)
        setSearchInput('')
        setFormData({
            sku: '',
            name: '',
            link: '',
            platform: activePlatform,
            brand: '',
            category: '',
        })
        setCompetitors([])
        setSelectedCompetitors(new Set())
    }

    const isValid = formData.sku && formData.name && formData.link

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Platform Toggle Tabs */}
            <div className="space-y-2">
                <Label>Platform</Label>
                <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
                    <button
                        type="button"
                        onClick={() => handlePlatformChange('blinkit')}
                        className={cn(
                            "px-4 py-1.5 text-sm font-medium rounded-md transition-colors",
                            activePlatform === 'blinkit'
                                ? "bg-background text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        Blinkit
                    </button>
                    <button
                        type="button"
                        onClick={() => handlePlatformChange('instamart')}
                        className={cn(
                            "px-4 py-1.5 text-sm font-medium rounded-md transition-colors",
                            activePlatform === 'instamart'
                                ? "bg-background text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        Instamart
                    </button>
                </div>
            </div>

            {/* Unified Search/URL Input */}
            <div className="space-y-2">
                <Label>Search product or paste URL</Label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder={activePlatform === 'instamart'
                            ? "Search by name or paste Instamart URL..."
                            : "Search by name or paste Blinkit URL..."
                        }
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value)
                            if (!isUrl(e.target.value)) {
                                setShowDropdown(true)
                            }
                        }}
                        onFocus={() => !isUrl(searchInput) && searchInput.length >= 2 && setShowDropdown(true)}
                        className="pl-10 pr-10"
                        disabled={!!selectedProduct}
                    />
                    {isSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {selectedProduct && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                        >
                            <X className="h-4 w-4 text-muted-foreground" />
                        </button>
                    )}

                    {/* Search Dropdown */}
                    {showDropdown && !selectedProduct && searchInput.length >= 2 && !isUrl(searchInput) && (
                        <div className="absolute top-full mt-2 w-full rounded-lg border bg-popover shadow-lg z-50 max-h-80 overflow-y-auto">
                            {isSearching ? (
                                <div className="p-4 space-y-2">
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                </div>
                            ) : searchResults.length > 0 ? (
                                <div className="py-2">
                                    {searchResults.map((product) => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-accent transition-colors"
                                            onClick={() => handleSelectProduct(product)}
                                        >
                                            {product.imageUrl ? (
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.name}
                                                    className="w-10 h-10 rounded object-cover"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                                                    <Package className="h-5 w-5 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{product.name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {product.brand} {product.category && `/ ${product.category}`}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-4 text-center text-muted-foreground text-sm">
                                    No products found for "{searchInput}"
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <p className="text-xs text-muted-foreground">
                    You can search by product name or paste a {activePlatform === 'instamart' ? 'Swiggy Instamart' : 'Blinkit'} product URL directly
                </p>
            </div>

            {/* Selected Product Preview */}
            {selectedProduct && (
                <div className="p-4 border border-primary/20 rounded-lg bg-primary/5">
                    <div className="flex items-center gap-3">
                        {selectedProduct.imageUrl ? (
                            <img
                                src={selectedProduct.imageUrl}
                                alt={selectedProduct.name}
                                className="w-16 h-16 rounded-lg object-cover"
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                                <Package className="h-8 w-8 text-muted-foreground" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground">{selectedProduct.name}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                {formData.brand || selectedProduct.brand}
                                {(formData.category || selectedProduct.category) &&
                                    ` / ${formData.category || selectedProduct.category}`}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono mt-1">
                                SKU: {selectedProduct.id}
                            </p>
                            {loadingCompetitors && (
                                <p className="text-xs text-primary mt-1 flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Loading details...
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Competitor Selection */}
            {selectedProduct && (
                <div className="space-y-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        <Label className="text-base font-bold">Add Competitors (Optional)</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Select competitor products to track alongside your product. Only products from other brands are shown.
                    </p>

                    {/* Competitor Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            ref={competitorInputRef}
                            type="text"
                            placeholder="Search for competitor products..."
                            value={competitorSearchQuery}
                            onChange={(e) => {
                                setCompetitorSearchQuery(e.target.value)
                                setShowCompetitorDropdown(true)
                            }}
                            onFocus={() => setShowCompetitorDropdown(true)}
                            className="pl-10"
                        />

                        {/* Competitor Search Dropdown */}
                        {showCompetitorDropdown && competitorSearchQuery.length >= 2 && competitorSuggestions.length > 0 && (
                            <div className="absolute top-full mt-2 w-full rounded-lg border bg-popover shadow-lg z-50 max-h-60 overflow-y-auto">
                                <div className="py-2">
                                    {competitorSuggestions.map((product) => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            className={cn(
                                                "flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-accent transition-colors",
                                                selectedCompetitors.has(product.id) && "bg-primary/10"
                                            )}
                                            onClick={() => handleAddCompetitorFromSearch(product)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedCompetitors.has(product.id)}
                                                onChange={() => { }}
                                                className="rounded border-border"
                                            />
                                            {product.imageUrl ? (
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.name}
                                                    className="w-8 h-8 rounded object-cover"
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                                    <Package className="h-4 w-4 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{product.name}</p>
                                                <p className="text-xs text-muted-foreground">{product.brand}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Suggested Competitors */}
                    {loadingCompetitors ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading competitor suggestions...
                        </div>
                    ) : competitors.length > 0 ? (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-muted-foreground">Suggested competitors:</p>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={competitors.length > 0 && selectedCompetitors.size === competitors.length}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                // Select all
                                                setSelectedCompetitors(new Set(competitors.map(c => c.id)))
                                            } else {
                                                // Deselect all
                                                setSelectedCompetitors(new Set())
                                            }
                                        }}
                                        className="rounded border-border"
                                    />
                                    <span className="text-primary font-bold">Select All</span>
                                </label>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                {competitors.map((product) => (
                                    <label
                                        key={product.id}
                                        className={cn(
                                            "flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors",
                                            selectedCompetitors.has(product.id)
                                                ? "border-primary bg-primary/5"
                                                : "border-border hover:bg-muted/50"
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedCompetitors.has(product.id)}
                                            onChange={() => handleCompetitorToggle(product)}
                                            className="rounded border-border"
                                        />
                                        {product.imageUrl ? (
                                            <img
                                                src={product.imageUrl}
                                                alt={product.name}
                                                className="w-8 h-8 rounded object-cover"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                                <Package className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{product.name}</p>
                                            <p className="text-xs text-muted-foreground">{product.brand}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {/* Selected Competitors Count */}
                    {selectedCompetitors.size > 0 && (
                        <p className="text-sm text-primary font-medium">
                            {selectedCompetitors.size} competitor{selectedCompetitors.size > 1 ? 's' : ''} selected
                        </p>
                    )}
                </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={!isValid}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add SKU
                </Button>
            </div>
        </form>
    )
}

// Edit Competitors Modal Component
function EditCompetitorsModal({
    sku,
    onClose,
    onAddCompetitors,
    onRemoveCompetitor,
    userBrand,
}: {
    sku: TrackedSKUWithCompetitors
    onClose: () => void
    onAddCompetitors: (competitors: CompetitorProduct[]) => Promise<void>
    onRemoveCompetitor: (competitorId: string) => Promise<void>
    userBrand: string
}) {
    const { selectedStore } = useStore()
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<Product[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set())
    const [isAdding, setIsAdding] = useState(false)
    const [removingId, setRemovingId] = useState<string | null>(null)

    const utils = trpc.useUtils()

    // Search for competitors
    useEffect(() => {
        if (searchQuery.length < 2) {
            setSearchResults([])
            return
        }

        const timer = setTimeout(async () => {
            setIsSearching(true)
            try {
                const result = await utils.search.products.fetch({
                    query: searchQuery,
                    lat: selectedStore.latitude,
                    lon: selectedStore.longitude,
                })
                // Filter out user's brand, existing competitors, and the product itself
                const existingIds = new Set([sku.externalId, ...sku.competitors.map(c => c.externalId)])
                const skuBrand = sku.brand?.toLowerCase() || ''
                const userBrandLower = userBrand.toLowerCase()

                const filtered = result.products.filter(p => {
                    const productBrand = p.brand?.toLowerCase() || ''
                    return (
                        !existingIds.has(p.id) &&
                        productBrand !== userBrandLower &&
                        productBrand !== skuBrand &&
                        productBrand !== ''
                    )
                })
                setSearchResults(filtered)
            } catch (error) {
                console.error('Search failed:', error)
            } finally {
                setIsSearching(false)
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery, selectedStore, sku, userBrand])

    const handleAddSelected = async () => {
        if (selectedToAdd.size === 0) return

        setIsAdding(true)
        const competitorsToAdd = searchResults
            .filter(p => selectedToAdd.has(p.id))
            .map(p => ({
                id: p.id,
                name: p.name,
                brand: p.brand || '',
                imageUrl: p.imageUrl || '',
            }))

        await onAddCompetitors(competitorsToAdd)
        setSelectedToAdd(new Set())
        setSearchQuery('')
        setSearchResults([])
        setIsAdding(false)
    }

    const handleRemove = async (competitorId: string) => {
        setRemovingId(competitorId)
        await onRemoveCompetitor(competitorId)
        setRemovingId(null)
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-background rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                <div className="p-4 border-b border-border flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold">Edit Competitors</h2>
                        <p className="text-sm text-muted-foreground">{sku.name}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-140px)]">
                    {/* Current Competitors */}
                    <div>
                        <h3 className="font-bold mb-2">Current Competitors ({sku.competitors.length})</h3>
                        {sku.competitors.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No competitors added yet</p>
                        ) : (
                            <div className="space-y-2">
                                {sku.competitors.map((comp) => (
                                    <div
                                        key={comp.id}
                                        className="flex items-center gap-3 p-3 border rounded-lg"
                                    >
                                        {comp.imageUrl ? (
                                            <img
                                                src={comp.imageUrl}
                                                alt={comp.name}
                                                className="w-10 h-10 rounded object-cover"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                                                <Package className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{comp.name}</p>
                                            <p className="text-xs text-muted-foreground">{comp.brand}</p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemove(comp.id)}
                                            disabled={removingId === comp.id}
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                        >
                                            {removingId === comp.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Add New Competitors */}
                    <div className="border-t pt-4">
                        <h3 className="font-bold mb-2">Add New Competitors</h3>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search for competitor products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>

                        {isSearching && (
                            <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Searching...
                            </div>
                        )}

                        {searchResults.length > 0 && (
                            <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-muted-foreground">
                                        {searchResults.length} results
                                    </p>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="checkbox"
                                            checked={searchResults.length > 0 && selectedToAdd.size === searchResults.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedToAdd(new Set(searchResults.map(p => p.id)))
                                                } else {
                                                    setSelectedToAdd(new Set())
                                                }
                                            }}
                                            className="rounded border-border"
                                        />
                                        <span className="text-primary font-medium">Select All</span>
                                    </label>
                                </div>
                                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                                    {searchResults.map((product) => (
                                        <label
                                            key={product.id}
                                            className={cn(
                                                "flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors",
                                                selectedToAdd.has(product.id)
                                                    ? "border-primary bg-primary/5"
                                                    : "border-border hover:bg-muted/50"
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedToAdd.has(product.id)}
                                                onChange={() => {
                                                    setSelectedToAdd(prev => {
                                                        const next = new Set(prev)
                                                        if (next.has(product.id)) {
                                                            next.delete(product.id)
                                                        } else {
                                                            next.add(product.id)
                                                        }
                                                        return next
                                                    })
                                                }}
                                                className="rounded border-border"
                                            />
                                            {product.imageUrl ? (
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.name}
                                                    className="w-8 h-8 rounded object-cover"
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                                    <Package className="h-4 w-4 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{product.name}</p>
                                                <p className="text-xs text-muted-foreground">{product.brand}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                {selectedToAdd.size > 0 && (
                                    <Button
                                        onClick={handleAddSelected}
                                        disabled={isAdding}
                                        className="w-full mt-2"
                                    >
                                        {isAdding ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add {selectedToAdd.size} Competitor{selectedToAdd.size > 1 ? 's' : ''}
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-4 border-t border-border">
                    <Button variant="outline" onClick={onClose} className="w-full">
                        Done
                    </Button>
                </div>
            </div>
        </div>
    )
}

// SKU Card Component with Competitors Dropdown
function SKUCard({
    sku,
    onToggle,
    onDelete,
    onEdit,
    onEditCompetitors,
    onRemoveCompetitor,
}: {
    sku: TrackedSKUWithCompetitors
    onToggle: () => void
    onDelete: () => void
    onEdit: (data: Partial<TrackedSKUWithCompetitors>) => void
    onEditCompetitors: () => void
    onRemoveCompetitor?: (competitorId: string) => void
}) {
    const [isEditing, setIsEditing] = useState(false)
    const [showCompetitors, setShowCompetitors] = useState(false)
    const [editData, setEditData] = useState({
        name: sku.name,
        link: sku.link || '',
        brand: sku.brand || '',
        category: sku.category || '',
    })

    const handleSave = () => {
        onEdit(editData)
        setIsEditing(false)
    }

    const handleCancel = () => {
        setEditData({
            name: sku.name,
            link: sku.link || '',
            brand: sku.brand || '',
            category: sku.category || '',
        })
        setIsEditing(false)
    }

    const platformLabel = platformOptions.find(p => p.value === sku.platform)?.label || sku.platform
    const hasCompetitors = sku.competitors && sku.competitors.length > 0

    return (
        <Card className={cn("transition-all", !sku.isActive && "opacity-60")}>
            <CardContent className="p-4">
                {isEditing ? (
                    <div className="space-y-3">
                        <Input
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            placeholder="Product Name"
                        />
                        <Input
                            value={editData.link}
                            onChange={(e) => setEditData({ ...editData, link: e.target.value })}
                            placeholder="Product Link"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <Input
                                value={editData.brand}
                                onChange={(e) => setEditData({ ...editData, brand: e.target.value })}
                                placeholder="Brand"
                            />
                            <Input
                                value={editData.category}
                                onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                                placeholder="Category"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={handleCancel}>
                                <X className="h-4 w-4" />
                            </Button>
                            <Button size="sm" onClick={handleSave}>
                                <Check className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-primary/10 text-primary">
                                        {platformLabel}
                                    </span>
                                    <span className="text-xs text-muted-foreground font-mono">
                                        {sku.externalId}
                                    </span>
                                </div>
                                <h3 className="font-semibold text-foreground truncate">{sku.name}</h3>
                                {(sku.brand || sku.category) && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {sku.brand && <span>{sku.brand}</span>}
                                        {sku.brand && sku.category && <span> / </span>}
                                        {sku.category && <span>{sku.category}</span>}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={onToggle}
                                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                                    title={sku.isActive ? 'Disable tracking' : 'Enable tracking'}
                                >
                                    {sku.isActive ? (
                                        <ToggleRight className="h-5 w-5 text-primary" />
                                    ) : (
                                        <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                                    )}
                                </button>
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                                    title="Edit"
                                >
                                    <Edit2 className="h-4 w-4 text-muted-foreground" />
                                </button>
                                <button
                                    onClick={onDelete}
                                    className="p-2 rounded-lg hover:bg-destructive/10 transition-colors"
                                    title="Delete"
                                >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                </button>
                            </div>
                        </div>

                        {sku.link && (
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                                <LinkIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <a
                                    href={sku.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary hover:underline truncate flex-1"
                                >
                                    {sku.link}
                                </a>
                                <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            </div>
                        )}

                        {/* View/Edit Competitors Dropdown */}
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={() => setShowCompetitors(!showCompetitors)}
                                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
                            >
                                <Users className="h-4 w-4" />
                                <span>
                                    {hasCompetitors
                                        ? `View Competitors (${sku.competitors.length})`
                                        : 'No Competitors'
                                    }
                                </span>
                                {showCompetitors ? (
                                    <ChevronUp className="h-4 w-4 ml-auto" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 ml-auto" />
                                )}
                            </button>

                            {showCompetitors && (
                                <div className="mt-2 space-y-2 pl-6 border-l-2 border-border">
                                    {hasCompetitors ? (
                                        <>
                                            {sku.competitors.map((comp) => (
                                                <div
                                                    key={comp.id}
                                                    className="flex items-center gap-2 p-2 rounded-lg bg-muted/30"
                                                >
                                                    {comp.imageUrl ? (
                                                        <img
                                                            src={comp.imageUrl}
                                                            alt={comp.name}
                                                            className="w-8 h-8 rounded object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                                            <Package className="h-4 w-4 text-muted-foreground" />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{comp.name}</p>
                                                        <p className="text-xs text-muted-foreground">{comp.brand}</p>
                                                    </div>
                                                    {onRemoveCompetitor && (
                                                        <button
                                                            onClick={() => onRemoveCompetitor(comp.id)}
                                                            className="p-1 rounded hover:bg-destructive/10 transition-colors"
                                                            title="Remove competitor"
                                                        >
                                                            <X className="h-3 w-3 text-destructive" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={onEditCompetitors}
                                                className="w-full mt-2"
                                            >
                                                <Plus className="h-3 w-3 mr-2" />
                                                Add More Competitors
                                            </Button>
                                        </>
                                    ) : (
                                        <div className="py-2">
                                            <p className="text-sm text-muted-foreground mb-3">
                                                No competitors added yet
                                            </p>
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={onEditCompetitors}
                                                className="w-full"
                                            >
                                                <Plus className="h-3 w-3 mr-2" />
                                                Add Competitors
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-muted-foreground mt-2">
                            Added {new Date(sku.createdAt).toLocaleDateString()}
                        </p>
                    </>
                )}
            </CardContent>
        </Card>
    )
}

// Empty State Component
function EmptyState({ onAddClick }: { onAddClick: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-border rounded-xl">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No SKUs tracked yet</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
                Add your product SKUs to track them across platforms. These will appear in the Competitor module.
            </p>
            <Button onClick={onAddClick}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First SKU
            </Button>
        </div>
    )
}

export function SettingsPage() {
    const {
        trackedSKUs,
        isLoading,
        error,
        workspaceId,
        addTrackedSKU,
        removeTrackedSKU,
        updateTrackedSKU,
        toggleSKUActive,
        addCompetitors,
        removeCompetitor,
        userBrand,
    } = useSettings()

    const [showAddForm, setShowAddForm] = useState(false)
    const [filterPlatform, setFilterPlatform] = useState<Platform | 'all'>('all')
    const [editingCompetitorsSku, setEditingCompetitorsSku] = useState<TrackedSKUWithCompetitors | null>(null)

    // Competitor scrape config state
    const [pinsPerCluster, setPinsPerCluster] = useState(5)
    const [darkstoresPerPin, setDarkstoresPerPin] = useState(1)
    const [isCompetitorEnabled, setIsCompetitorEnabled] = useState(false)
    const [isCompetitorToggleSaving, setIsCompetitorToggleSaving] = useState(false)
    const [competitorToggleStatus, setCompetitorToggleStatus] = useState<'idle' | 'success' | 'error'>('idle')
    const [isScrapeConfigLoading, setIsScrapeConfigLoading] = useState(true)
    const [isScrapeConfigSaving, setIsScrapeConfigSaving] = useState(false)
    const [scrapeConfigSaveStatus, setScrapeConfigSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const settingsUtils = trpc.useUtils()
    const updateCompetitorSettingsMutation = trpc.competitorScrape.updateSettings.useMutation()

    useEffect(() => {
        if (!workspaceId) return
        const loadScrapeConfig = async () => {
            try {
                setIsScrapeConfigLoading(true)
                const settings = await settingsUtils.competitorScrape.getSettings.fetch({ workspaceId })
                if (settings) {
                    setPinsPerCluster(settings.pinsPerCluster ?? 5)
                    setDarkstoresPerPin(settings.darkstoresPerPin ?? 1)
                    setIsCompetitorEnabled(settings.isEnabled)
                }
            } catch (err) {
                console.error('Failed to load competitor scrape config:', err)
            } finally {
                setIsScrapeConfigLoading(false)
            }
        }
        loadScrapeConfig()
    }, [workspaceId])

    const handleSaveScrapeConfig = async () => {
        if (!workspaceId) return
        try {
            setIsScrapeConfigSaving(true)
            await updateCompetitorSettingsMutation.mutateAsync({
                workspaceId,
                pinsPerCluster: pinsPerCluster,
                darkstoresPerPin: darkstoresPerPin,
            })
            setScrapeConfigSaveStatus('success')
            setTimeout(() => setScrapeConfigSaveStatus('idle'), 3000)
        } catch (err) {
            console.error('Failed to save competitor scrape config:', err)
            setScrapeConfigSaveStatus('error')
            setTimeout(() => setScrapeConfigSaveStatus('idle'), 3000)
        } finally {
            setIsScrapeConfigSaving(false)
        }
    }

    const handleToggleCompetitor = async (nextEnabled: boolean) => {
        if (!workspaceId) return
        const previous = isCompetitorEnabled
        setIsCompetitorEnabled(nextEnabled)
        try {
            setIsCompetitorToggleSaving(true)
            await updateCompetitorSettingsMutation.mutateAsync({ workspaceId, isEnabled: nextEnabled })
            setCompetitorToggleStatus('success')
            setTimeout(() => setCompetitorToggleStatus('idle'), 3000)
        } catch (err) {
            console.error('Failed to update competitor scrape status:', err)
            setIsCompetitorEnabled(previous)
            setCompetitorToggleStatus('error')
            setTimeout(() => setCompetitorToggleStatus('idle'), 3000)
        } finally {
            setIsCompetitorToggleSaving(false)
        }
    }

    const filteredSKUs = filterPlatform === 'all'
        ? trackedSKUs
        : trackedSKUs.filter(sku => sku.platform === filterPlatform)

    const handleAddSKU = async (data: AddSKUFormData, competitors?: CompetitorProduct[]) => {
        try {
            console.log('[handleAddSKU] Form data:', data)
            console.log('[handleAddSKU] Competitors:', competitors)

            const competitorData = competitors?.map(c => ({
                externalId: c.id,
                name: c.name,
                brand: c.brand,
                imageUrl: c.imageUrl,
                price: c.price,
                mrp: c.mrp,
                discountPercent: c.discountPercent,
                rating: c.rating,
                ratingCount: c.ratingCount,
                quantity: c.quantity,
                unit: c.unit,
            }))

            console.log('[handleAddSKU] Competitor data to save:', competitorData)

            await addTrackedSKU(data, competitorData)
            setShowAddForm(false)
        } catch (err) {
            console.error('Failed to add SKU:', err)
        }
    }

    const handleEditCompetitors = (sku: TrackedSKUWithCompetitors) => {
        setEditingCompetitorsSku(sku)
    }

    const handleAddCompetitorsToSku = async (competitors: CompetitorProduct[]) => {
        if (!editingCompetitorsSku) return

        const competitorData = competitors.map(c => ({
            externalId: c.id,
            name: c.name,
            brand: c.brand,
            imageUrl: c.imageUrl,
            price: c.price,
            mrp: c.mrp,
            discountPercent: c.discountPercent,
            rating: c.rating,
            ratingCount: c.ratingCount,
            quantity: c.quantity,
            unit: c.unit,
        }))

        const updated = await addCompetitors(editingCompetitorsSku.id, competitorData)
        if (updated) {
            setEditingCompetitorsSku(updated)
        }
    }

    const handleRemoveCompetitorFromSku = async (competitorId: string) => {
        if (!editingCompetitorsSku) return

        const updated = await removeCompetitor(editingCompetitorsSku.id, competitorId)
        if (updated) {
            setEditingCompetitorsSku(updated)
        }
    }

    // Count SKUs by platform
    const skuCounts = trackedSKUs.reduce((acc, sku) => {
        acc[sku.platform] = (acc[sku.platform] || 0) + 1
        return acc
    }, {} as Record<string, number>)

    return (
        <div className="p-6 md:p-8 w-full animate-fade-in">
            <div className="max-w-[1000px] mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Settings2 className="h-8 w-8 text-primary" />
                        <h1 className="app-page-title">Competitor Settings</h1>
                    </div>
                    <p className="app-page-subtitle">
                        Configure competitor tracking, cities, and platform integrations.
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
                        {error}
                    </div>
                )}

                {/* Competitor Cities */}
                {workspaceId && (
                    <div className="mb-6">
                        <VisibilityKeywordsCitiesSettings
                            workspaceId={workspaceId}
                            showKeywords={false}
                            citySettingKey="competitorEnabledCityIds"
                            citySectionTitle="Competitor Cities"
                            citySectionDescription="Select geo cities only. Clusters are auto-derived from store mappings in this phase."
                            citySaveButtonLabel="Save Competitor Cities"
                        />
                    </div>
                )}

                {/* Competitor Scraping Status */}
                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Activity className="h-5 w-5 text-primary" />
                                <div>
                                    <CardTitle className="text-base font-bold">Competitor Scraping</CardTitle>
                                    <CardDescription>
                                        Enable or disable automated competitor scraping for this workspace.
                                    </CardDescription>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span
                                    className={`text-sm font-medium ${isCompetitorEnabled ? 'text-green-600' : 'text-muted-foreground'}`}
                                >
                                    {isCompetitorEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <Switch
                                    checked={isCompetitorEnabled}
                                    onCheckedChange={handleToggleCompetitor}
                                    disabled={isScrapeConfigLoading || isCompetitorToggleSaving}
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {isCompetitorToggleSaving && <p className="text-sm text-muted-foreground">Saving...</p>}
                        {competitorToggleStatus === 'success' && !isCompetitorToggleSaving && (
                            <p className="text-sm text-foreground">Saved</p>
                        )}
                        {competitorToggleStatus === 'error' && !isCompetitorToggleSaving && (
                            <p className="text-sm text-destructive">Failed to save status.</p>
                        )}
                    </CardContent>
                </Card>

                {/* Competitor Scrape Configuration */}
                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Activity className="h-5 w-5 text-primary" />
                                <div>
                                    <CardTitle className="text-base font-bold">Competitor Scrape Configuration</CardTitle>
                                    <CardDescription>
                                        Configure how many PINs per cluster and darkstores per PIN to scrape.
                                        Scraping happens across all clusters in each selected city.
                                    </CardDescription>
                                </div>
                            </div>
                            <Button onClick={handleSaveScrapeConfig} disabled={isScrapeConfigSaving} size="sm">
                                {isScrapeConfigSaving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : scrapeConfigSaveStatus === 'success' ? (
                                    <>
                                        <Check className="h-4 w-4 mr-2" />
                                        Saved!
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Config
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {isScrapeConfigLoading ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="pins-per-cluster">PINs per Cluster</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Number of PIN codes to scrape in each cluster (1-50)
                                    </p>
                                    <Input
                                        id="pins-per-cluster"
                                        type="number"
                                        min={1}
                                        max={50}
                                        value={pinsPerCluster}
                                        onChange={(e) => setPinsPerCluster(Math.max(1, Math.min(50, Number(e.target.value))))}
                                        className="max-w-xs"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="darkstores-per-pin">Darkstores per PIN</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Number of darkstores to scrape in each PIN code (1-20)
                                    </p>
                                    <Input
                                        id="darkstores-per-pin"
                                        type="number"
                                        min={1}
                                        max={20}
                                        value={darkstoresPerPin}
                                        onChange={(e) => setDarkstoresPerPin(Math.max(1, Math.min(20, Number(e.target.value))))}
                                        className="max-w-xs"
                                    />
                                </div>
                            </div>
                        )}
                        {scrapeConfigSaveStatus === 'error' && (
                            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                                Failed to save scrape configuration. Please try again.
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Blinkit Configuration */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-base font-bold">Product Tracking</CardTitle>
                                <CardDescription>
                                    Add SKUs to track across platforms. These products will appear in the Competitor module.
                                </CardDescription>
                            </div>
                            {trackedSKUs.length > 0 && !showAddForm && (
                                <Button onClick={() => setShowAddForm(true)}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add SKU
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* Add Form */}
                        {showAddForm && (
                            <div className="mb-6 p-4 border border-border rounded-lg bg-muted/20">
                                <h3 className="font-semibold text-foreground mb-4">Add New SKU</h3>
                                <AddSKUForm
                                    onSubmit={handleAddSKU}
                                    onCancel={() => setShowAddForm(false)}
                                    userBrand={userBrand}
                                    workspaceId={workspaceId}
                                />
                            </div>
                        )}

                        {/* Loading State */}
                        {isLoading && (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            </div>
                        )}

                        {/* Stats Bar */}
                        {!isLoading && trackedSKUs.length > 0 && (
                            <div className="flex flex-wrap items-center gap-4 mb-6 p-3 bg-muted/30 rounded-lg">
                                <div className="text-sm">
                                    <span className="text-muted-foreground">Total SKUs:</span>{' '}
                                    <span className="font-semibold text-foreground">{trackedSKUs.length}</span>
                                </div>
                                <div className="text-sm">
                                    <span className="text-muted-foreground">Active:</span>{' '}
                                    <span className="font-semibold text-primary">
                                        {trackedSKUs.filter(s => s.isActive).length}
                                    </span>
                                </div>
                                {Object.entries(skuCounts).map(([platform, count]) => (
                                    <div key={platform} className="text-sm">
                                        <span className="text-muted-foreground capitalize">{platform}:</span>{' '}
                                        <span className="font-semibold text-foreground">{count}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Filter by platform */}
                        {!isLoading && trackedSKUs.length > 0 && (
                            <div className="flex items-center gap-2 mb-4">
                                <span className="text-sm text-muted-foreground">Filter:</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setFilterPlatform('all')}
                                        className={cn(
                                            "px-3 py-1 text-sm rounded-md transition-colors",
                                            filterPlatform === 'all'
                                                ? "bg-primary text-primary-foreground"
                                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                                        )}
                                    >
                                        All
                                    </button>
                                    {platformOptions.map((opt) => (
                                        <button
                                            key={opt.value}
                                            onClick={() => setFilterPlatform(opt.value)}
                                            className={cn(
                                                "px-3 py-1 text-sm rounded-md transition-colors",
                                                filterPlatform === opt.value
                                                    ? "bg-primary text-primary-foreground"
                                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                                            )}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* SKU List */}
                        {!isLoading && trackedSKUs.length === 0 ? (
                            <EmptyState onAddClick={() => setShowAddForm(true)} />
                        ) : !isLoading && filteredSKUs.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No SKUs found for the selected platform.
                            </div>
                        ) : !isLoading && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {filteredSKUs.map((sku) => (
                                    <SKUCard
                                        key={sku.id}
                                        sku={sku}
                                        onToggle={() => toggleSKUActive(sku.id)}
                                        onDelete={() => removeTrackedSKU(sku.id)}
                                        onEdit={(data) => updateTrackedSKU(sku.id, {
                                            name: data.name,
                                            link: data.link || undefined,
                                            brand: data.brand || undefined,
                                            category: data.category || undefined,
                                        })}
                                        onEditCompetitors={() => handleEditCompetitors(sku)}
                                        onRemoveCompetitor={(compId) => removeCompetitor(sku.id, compId)}
                                    />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Future Platform Cards */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {['Zepto', 'BBNow'].map((platform) => (
                        <Card key={platform} className="opacity-50">
                            <CardContent className="p-4 flex items-center justify-between">
                                <span className="font-medium text-muted-foreground">{platform}</span>
                                <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                                    Coming Soon
                                </span>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Edit Competitors Modal */}
            {editingCompetitorsSku && (
                <EditCompetitorsModal
                    sku={editingCompetitorsSku}
                    onClose={() => setEditingCompetitorsSku(null)}
                    onAddCompetitors={handleAddCompetitorsToSku}
                    onRemoveCompetitor={handleRemoveCompetitorFromSku}
                    userBrand={userBrand}
                />
            )}
        </div>
    )
}
